import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "../constants/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  LucideTrash,
  LucidePlus,
  LucideEdit,
  LucideUser,
  LucideShare2,
  LucideChevronLeft,
  LucideChevronRight,
  LucideDownload,
  LucideSettings2,
} from "lucide-react";
import { REPORT_FIELD_LABELS } from "../constants/reportFieldLabels";
import { ExportModal } from "@/components/ExportModal";
import { ShareClassModal } from "@/components/ShareClassModal";
import useSubscriptionInfo from "@/hooks/useSubscriptionInfo";

const REPORTS_PAGE_SIZE = 10;

export const Class = () => {
  const { reportGroupId } = useParams();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [classData, setClassData] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [studentName, setStudentName] = useState("");
  const [studentOen, setStudentOen] = useState("");
  const [applyDefaults, setApplyDefaults] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);

  const [reportsPage, setReportsPage] = useState(0);
  const [sortField, setSortField] = useState("oen");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalReports, setTotalReports] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [showExportModal, setShowExportModal] = useState(false);
  const [allReportsForExport, setAllReportsForExport] = useState([]);

  const { reportsUsed, maxReports, loading: subLoading } = useSubscriptionInfo();
  const atLimit = !subLoading && reportsUsed >= maxReports;

  const fetchClass = async () => {
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        sort: sortField,
        order: sortOrder,
        search: debouncedSearch,
        page: reportsPage,
        limit: REPORTS_PAGE_SIZE,
      });
      const res = await fetch(
        `${BACKEND_URL}api/report-groups/${reportGroupId}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to fetch class");
      const data = await res.json();
      setClassData(data);
      setReports(data.reports || []);
      setTotalReports(data.totalReports ?? 0);
      setTotalPages(data.totalPages ?? 1);
      setFetchError(null);
    } catch (err) {
      console.error("Error fetching class:", err);
      setFetchError("Could not load this class. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 0 when sort or search changes
  useEffect(() => {
    setReportsPage(0);
  }, [sortField, sortOrder, debouncedSearch]);

  // Fetch whenever group, sort, search, or page changes
  useEffect(() => {
    fetchClass();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportGroupId, sortField, sortOrder, debouncedSearch, reportsPage]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!studentName) return;
    setCreating(true);
    setCreateError("");
    try {
      const token = await getToken();
      const body = { studentName, applyDefaults };
      if (studentOen.trim()) body.oen = studentOen.trim();
      const res = await fetch(
        `${BACKEND_URL}api/report-groups/${reportGroupId}/reports`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );
      if (res.status === 403) {
        setCreateError("Report card quota exceeded. Please upgrade your plan.");
        return;
      }
      if (!res.ok) throw new Error("Failed to create report card");
      const newCard = await res.json();
      setStudentName("");
      setStudentOen("");
      setApplyDefaults(true);
      setShowForm(false);
      navigate(`/classes/${reportGroupId}/reports/${newCard._id}`);
    } catch (err) {
      console.error("Error creating report card:", err);
      setCreateError("Failed to create report card. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (card) => {
    if (!confirm(`Delete report card for ${card.studentName}?`)) return;
    try {
      const token = await getToken();
      await fetch(`${BACKEND_URL}api/reports/${card._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports((prev) => prev.filter((r) => r._id !== card._id));
    } catch (err) {
      console.error("Error deleting report card:", err);
      toast.error("Failed to delete report card. Please try again.");
    }
  };

  const fieldLabels = REPORT_FIELD_LABELS[classData?.reportType] ?? [];

  const openExportModal = async () => {
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        sort: sortField,
        order: sortOrder,
        limit: 0,
      });
      const res = await fetch(
        `${BACKEND_URL}api/report-groups/${reportGroupId}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to fetch reports");
      const data = await res.json();
      setAllReportsForExport(data.reports || []);
    } catch {
      setAllReportsForExport(reports);
    }
    setShowExportModal(true);
  };

  if (loading) {
    return (
      <div className="dashboard-content text-muted-foreground">Loading...</div>
    );
  }

  if (fetchError) {
    return (
      <div className="dashboard-content">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {fetchError}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <Link
        to="/classes"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <LucideChevronLeft size={14} />
        My Classes
      </Link>

      <div className="dashboard-welcome">
        <div>
          <h1 className="dashboard-heading">
            {[
              classData?.grade ? `Grade ${classData.grade}` : null,
              classData?.schoolYear || null,
            ]
              .filter(Boolean)
              .join(" · ") ||
              classData?.name ||
              "Class"}
          </h1>
          {classData?.name && (
            <p className="dashboard-subheading">{classData.name}</p>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {atLimit && (
            <span className="text-sm text-destructive">
              Report card limit reached.{" "}
              <Link to="/billing" className="underline">
                Upgrade
              </Link>
            </span>
          )}
          {reports.length > 0 && (
            <Button variant="outline" onClick={openExportModal}>
              <LucideDownload className="mr-2" size={16} />
              Export
            </Button>
          )}
          {fieldLabels.length > 0 && (
            <Button
              variant="outline"
              onClick={() =>
                navigate(`/classes/${reportGroupId}/defaults`)
              }
            >
              <LucideSettings2 className="mr-2" size={16} />
              Report Defaults
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowShareModal(true)}>
            <LucideShare2 className="mr-2" size={16} />
            Share
          </Button>
          <Button onClick={() => setShowForm((v) => !v)} disabled={atLimit}>
            <LucidePlus className="mr-2" size={16} />
            New Report Card
          </Button>
        </div>
      </div>

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="border border-green-200 rounded-lg p-4 bg-green-50 dark:bg-green-950/20 dark:border-green-900 mb-6 space-y-3"
        >
          <h2 className="text-base font-semibold text-foreground">
            New Report Card
          </h2>
          <div className="flex flex-wrap gap-3">
            <div className="space-y-1 flex-1 min-w-[160px] max-w-xs">
              <Label>Student Name *</Label>
              <Input
                placeholder="e.g. Jane Smith"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1 flex-1 min-w-[140px] max-w-xs">
              <Label>
                OEN{" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <Input
                placeholder="e.g. 123456789"
                value={studentOen}
                onChange={(e) => setStudentOen(e.target.value)}
              />
            </div>
          </div>
          {Object.keys(classData?.reportDefaults ?? {}).length > 0 && (
            <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
              <input
                type="checkbox"
                checked={applyDefaults}
                onChange={(e) => setApplyDefaults(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary"
              />
              <span className="text-sm text-foreground">
                Apply class defaults to this report
              </span>
            </label>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={creating}>
              {creating ? "Creating..." : "Create"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowForm(false);
                setStudentOen("");
                setApplyDefaults(true);
              }}
            >
              Cancel
            </Button>
          </div>
          {createError && (
            <p className="text-sm text-red-600">{createError}</p>
          )}
        </form>
      )}

      {/* Search + sort controls */}
      {(totalReports > 0 || searchQuery) && (
        <div className="flex flex-wrap gap-2 mb-4">
          <Input
            placeholder="Search by name or OEN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={`${sortField}:${sortOrder}`}
            onChange={(e) => {
              const [field, ord] = e.target.value.split(":");
              setSortField(field);
              setSortOrder(ord);
            }}
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground"
          >
            <option value="oen:asc">OEN (A-Z)</option>
            <option value="oen:desc">OEN (Z-A)</option>
            <option value="name:asc">Name (A-Z)</option>
            <option value="name:desc">Name (Z-A)</option>
            <option value="updatedAt:desc">Recently edited</option>
            <option value="updatedAt:asc">Oldest edited</option>
          </select>
        </div>
      )}

      {reports.length === 0 && !searchQuery && (
        <div className="text-center py-16 text-muted-foreground">
          <LucideUser size={40} className="mx-auto mb-3 opacity-40" />
          <p>No report cards yet. Add students to get started.</p>
        </div>
      )}

      {reports.length === 0 && searchQuery && (
        <div className="text-center py-12 text-muted-foreground">
          <p>No students match &quot;{searchQuery}&quot;.</p>
        </div>
      )}

      <div className="space-y-2">
        {reports.map((card) => (
          <div
            key={card._id}
            className="border border-border rounded-lg p-4 bg-primary/5 flex items-center justify-between"
          >
            <Link
              to={`/classes/${reportGroupId}/reports/${card._id}`}
              className="flex-1 min-w-0"
            >
              <div className="flex items-center gap-3 px-3 py-1.5">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <LucideUser size={18} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground leading-tight">
                    {card.studentName}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {card.data?.oen?.trim() ? (
                      `OEN ${card.data.oen}`
                    ) : (
                      <span className="italic opacity-60">OEN not set</span>
                    )}
                  </p>
                </div>
              </div>
            </Link>
            <div className="flex gap-2">
              <Link to={`/classes/${reportGroupId}/reports/${card._id}`}>
                <Button size="sm">
                  <LucideEdit className="mr-1" size={14} />
                  Edit
                </Button>
              </Link>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDelete(card)}
              >
                <LucideTrash className="mr-1" size={14} />
                Delete
              </Button>
            </div>
          </div>
        ))}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReportsPage((p) => p - 1)}
              disabled={reportsPage === 0}
              className="gap-1"
            >
              <LucideChevronLeft size={14} />
              Prev
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {reportsPage + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReportsPage((p) => p + 1)}
              disabled={reportsPage === totalPages - 1}
              className="gap-1"
            >
              Next
              <LucideChevronRight size={14} />
            </Button>
          </div>
        )}
      </div>

      <ExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        reports={allReportsForExport}
        classData={classData}
      />

      <ShareClassModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        reportGroupId={reportGroupId}
        classData={classData}
        onClassDataChange={setClassData}
        onRefresh={fetchClass}
      />
    </div>
  );
};
